<template>
  <div
    ref="wrapper"
    class="flex"
    :data-cy="`field-${id}`"
    :class="
      displayAsColumn ? 'flex-col space-y-2' : 'flex-row items-center space-x-2'
    "
  >
    <span>
      {{ label }}
    </span>
    <div
      v-tooltip="{
        content: valueAsString,
        onShow: () => textOverflows,
      }"
      class="flex w-full min-w-0 items-start space-x-2"
    >
      <div ref="textContainer" class="min-w-0">
        <info-span-copy
          :text="valueAsString"
          :disabled="!clickToCopy"
          :data-cy="id"
          class="flex w-full min-w-0 justify-start text-gray-700"
        />

        <span
          ref="text"
          class="relative -z-5 whitespace-nowrap text-transparent"
        >
          {{ valueAsString || "-" }}
        </span>
      </div>
      <template v-if="id.includes('phone') && value && value !== '-'">
        <contact-phone-actions
          v-if="!isBadNumberOrDnC && canShowActions"
          :phone-number="valueAsString.toString()"
        />
        <block-phone-actions
          class="mt-[10px]"
          :telephone-info="telephoneStatusInfo"
          :contact-name="phoneContactName"
          :is-business="isBusiness"
        />
      </template>
      <block-email-action
        v-if="isEmailField && value && value !== '-' && canShowActions"
        class="mt-2-5"
        :email="String(valueAsString)"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";

import { TelephoneStatus } from "@/enums/communicationLogs";
import { formatUserFullName } from "@/helpers/formatting";
import { useDealsBase } from "@/hooks/deals";
import { useDealTelephones } from "@/hooks/deals";

import ContactPhoneActions from "@/components/communication/ContactPhoneActions.vue";
import BlockEmailAction from "@/views/deals/components/BlockEmailAction.vue";
import BlockPhoneActions from "@/views/deals/components/BlockPhoneActions.vue";
import InfoSpanCopy from "@/views/deals/components/InfoSpanCopy.vue";

import type { IOwner } from "@/models/users";

interface Props {
  /**
   * Allow user to click to copy field value
   */
  clickToCopy?: boolean;
  /**
   * Identifier for copy function
   */
  id?: string;
  /**
   * Required label for field
   */
  label: string;
  /**
   * Required value for field
   */
  value: string | number | string[];
  isBusiness?: boolean;
  displayAsColumn?: boolean;
  canShowActions?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  clickToCopy: false,
  id: "",
  label: "",
  value: "",
  isBusiness: false,
  displayAsColumn: true,
  canShowActions: false,
});

const { activeDeal } = useDealsBase();

const { getTelephoneStatusInfo } = useDealTelephones(activeDeal);

const wrapper = ref<HTMLElement | null>(null);
const textContainer = ref<HTMLElement | null>(null);
const text = ref<HTMLElement | null>(null);

const telephoneStatusInfo = computed(() =>
  getTelephoneStatusInfo(props.value?.toString(), props.isBusiness)
);

const isBadNumberOrDnC = computed(() =>
  [TelephoneStatus.BadNumber, TelephoneStatus.DoNotContact].some(
    (status) => status === telephoneStatusInfo.value?.status
  )
);

const valueAsString = computed(() =>
  Array.isArray(props.value) ? props.value.join(", ") : props.value
);

const isEmailField = computed(() => props.id.includes("email"));

const textOverflows = computed(
  () =>
    !!textContainer.value &&
    !!text.value &&
    !wrapper.value?.classList?.contains("wrap-text") &&
    text.value.offsetWidth > textContainer.value.clientWidth
);

const isOwnerPhone = (owner: IOwner | null) =>
  !!owner?.all_telephones
    ?.map(({ number }) => number)
    .includes(props.value?.toString());

const phoneContactName = computed(() => {
  if (props.isBusiness) {
    return activeDeal.value?.business?.business_legal_name || "";
  }

  const isMainOwnerPhone = isOwnerPhone(activeDeal.value.personal_information);

  if (isMainOwnerPhone) {
    return formatUserFullName(activeDeal.value.personal_information);
  }

  const ownerWithPhone =
    activeDeal.value?.business?.other_owners?.find(isOwnerPhone) || null;

  return formatUserFullName(ownerWithPhone);
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.flex-row {
  & > span {
    @apply w-1/2 shrink;
  }
  & > div {
    @apply items-center justify-end;
    & .items-start {
      @apply items-center;
    }
    & .text-transparent {
      @apply hidden;
    }
  }
}
</style>
